/**
 * 看板数据入口。静态阶段走 mock，接口就绪后只改这一个文件。
 *
 * 切到真实数据时：
 *  1. 把 buildMockData 换成 useQuery(BAG_STAY_MONITOR_QUERY, { variables: { hub } })
 *  2. 把 fetchDetails 换成 apolloClient.query(BAG_STAY_MONITOR_DETAIL_QUERY, { variables: q })
 *  3. 把 exportDetails 换成导出 mutation（后端异步生成，返回文件名）
 *  4. 删掉 mock/ 目录
 * 页面组件与 lib/tree.ts 的聚合逻辑不需要改动。
 */
import { useCallback, useEffect, useState } from 'react';
import type { BagStayMonitorData, DetailQuery, ParcelDetail } from './types';
import { buildMockData, buildMockDetails } from './mock/mockData';

/** 线上是 10 分钟缓存 + 后台刷新，这里沿用同一个节奏 */
export const REFRESH_INTERVAL_MS = 10 * 60 * 1000;

/** 加载失败但拿不到具体原因时的 error 值，页面据此显示 i18n 的 loadError */
export const LOAD_FAILED = 'LOAD_FAILED';

/** mock 查询的模拟耗时，让骨架屏和 loading 态能被看到 */
const MOCK_LATENCY_MS = 320;

interface UseBagStayMonitorDataResult {
    data: BagStayMonitorData | null;
    loading: boolean;
    error: string;
    refresh: () => void;
    fetchDetails: (q: DetailQuery) => Promise<ParcelDetail[]>;
    /** 包裹明细导出：后端按查询条件异步生成文件，返回文件名；null = 导出接口还没有 */
    exportDetails: (q: DetailQuery) => Promise<string | null>;
}

export function useBagStayMonitorData(hub: string): UseBagStayMonitorDataResult {
    const [data, setData] = useState<BagStayMonitorData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError('');
        const timer = setTimeout(() => {
            if (cancelled) return;
            try {
                setData(buildMockData(hub, Date.now()));
            } catch (e) {
                // 没有具体原因时置一个占位，页面按 loadError 文案展示（hook 里不放可见文案）
                setError(e instanceof Error && e.message ? e.message : LOAD_FAILED);
            } finally {
                setLoading(false);
            }
        }, MOCK_LATENCY_MS);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [hub, reloadKey]);

    // 10 分钟自动刷新
    useEffect(() => {
        const id = setInterval(() => setReloadKey((k) => k + 1), REFRESH_INTERVAL_MS);
        return () => clearInterval(id);
    }, []);

    const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

    const fetchDetails = useCallback(
        (q: DetailQuery) =>
            new Promise<ParcelDetail[]>((resolve) => {
                setTimeout(() => resolve(buildMockDetails(q, Date.now())), MOCK_LATENCY_MS);
            }),
        []
    );

    // todo hetianyang 后端导出 mutation 就绪后替换，mock 阶段没有文件可下
    const exportDetails = useCallback((_q: DetailQuery) => Promise.resolve<string | null>(null), []);

    return { data, loading, error, refresh, fetchDetails, exportDetails };
}
